import * as React from "react";
import { useTranslation } from "react-i18next";
import sanitizeHtml from "sanitize-html";
import type { Speaker, ThemeKind } from "src/types";

import { CoreRichEditor } from "@/components/editor/primitives/rich-text";
import { getSocialLinkAriaLabel } from "@/components/editor/shared/speakers/utils";
import useTheme from "@/components/editor/themes/use-theme";
import { LinkedInSquare } from "@/components/icons/linkedin-square";
import { Twitter } from "@/components/icons/twitter";
import { Website } from "@/components/icons/website";

import {
  Actions,
  BioField,
  Container,
  Footer,
  Header,
  HeadlineField,
  NameField,
  Overview,
  ScrollWrapper,
  Thumbnail,
} from "./speaker-details-styles";

export function SpeakerDetails({
  name,
  bio,
  headline,
  social,
  image_url,
}: Omit<Speaker, "id" | "first_name" | "last_name">) {
  const [isBioScrolled, setIsBioScrolled] = React.useState(false);

  const rootRef = React.useRef(null);
  const targetRef = React.useRef(null);
  const { currentTheme } = useTheme();

  React.useEffect(() => {
    const { current: root } = rootRef;
    const { current: target } = targetRef;

    if (!root || !target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isTargetVisible = entry.intersectionRatio > 0;
          setIsBioScrolled(!isTargetVisible);
        });
      },
      { root }
    );
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, []);

  const sanitize = (dirty: string) => ({
    __html: sanitizeHtml(dirty, {
      allowedTags: ["br", "div"],
      allowedAttributes: {},
    }),
  });

  return (
    <Container
      className={currentTheme.kind}
      data-testid="speaker-details-container"
    >
      <Header scrolled={isBioScrolled}>
        <div className="flex">
          <Thumbnail
            className={currentTheme.kind}
            alt={name}
            draggable="false"
            src={image_url}
          />
          <Overview>
            <NameField
              className={currentTheme.kind}
              data-testid="speaker-name-field"
              dangerouslySetInnerHTML={sanitize(name)}
            />
            {headline && (
              <HeadlineField
                className={currentTheme.kind}
                data-testid="speaker-headline-field"
                dangerouslySetInnerHTML={sanitize(headline)}
              />
            )}
            <SocialLinks
              name={name}
              social={social}
              themeKind={currentTheme.kind}
            />
          </Overview>
        </div>
      </Header>

      <Footer>
        <ScrollWrapper data-body-scroll-lock-ignore ref={rootRef}>
          <div ref={targetRef}></div>
          <BioField
            data-testid="speaker-bio-field"
            className={currentTheme.kind}
          >
            <CoreRichEditor text={bio} isReadonly />
          </BioField>
        </ScrollWrapper>
      </Footer>
    </Container>
  );
}

const SocialLinks = ({
  name,
  social,
  themeKind,
}: {
  name: string;
  social: Speaker["social"];
  themeKind: ThemeKind;
}) => {
  const { t } = useTranslation();

  return (
    <Actions className={themeKind}>
      {social?.linkedin_url && (
        <a
          href={social.linkedin_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(name, "LinkedIn", t)}
        >
          <LinkedInSquare className="size-[18px]" aria-label="LinkedIn" />
        </a>
      )}
      {social?.twitter_url && (
        <a
          href={social.twitter_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(name, "Twitter", t)}
        >
          <Twitter className="size-[18px]" aria-label="Twitter" />
        </a>
      )}
      {social?.website_url && (
        <a
          href={social.website_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(name, "Website", t)}
        >
          <Website className="size-[18px] text-black" aria-label="Website" />
        </a>
      )}
    </Actions>
  );
};
